import type { DensityConfigOverrides } from './build-config';
import { PATTERN_PICKER_MODES } from './pattern-groups';
import type { DotStudioMode, SourceLayout } from './types';

/** Fixed hero-picker alternates — distinct pattern families from the random slot. */
export const HERO_COVER_ALT_MODES: readonly [DotStudioMode, DotStudioMode, DotStudioMode] = [
  'horizon',
  'ripple',
  'territory',
];

function pick<T>(items: readonly T[], rng: () => number): T {
  const item = items[Math.floor(rng() * items.length)];
  if (item === undefined) {
    throw new Error('randomize-pattern: empty pick list');
  }
  return item;
}

function rnd(min: number, max: number, rng: () => number): number {
  return min + rng() * (max - min);
}

/** Seed string format used by v9.1 Dot Studio (`morfi-123456`). */
export function randomMorfiSeedText(rng: () => number = Math.random): string {
  return `morfi-${String(Math.floor(rng() * 999_999))}`;
}

function randomSourceOverrides(
  rng: () => number,
): Pick<
  DensityConfigOverrides,
  'srcLayout' | 'srcCount' | 'srcSize' | 'srcElong' | 'srcFalloff' | 'srcDivide'
> {
  const layouts: readonly SourceLayout[] = ['center', 'pair', 'row', 'scatter', 'corners', 'ring'];
  return {
    srcLayout: pick(layouts, rng),
    srcCount: pick([1, 2, 3, 4, 6, 8, 10] as const, rng),
    srcSize: Number(rnd(0.3, 0.8, rng).toFixed(2)),
    srcElong: Number(rnd(1, 3, rng).toFixed(1)),
    srcFalloff: Number(rnd(0.3, 0.7, rng).toFixed(2)),
    srcDivide: pick([0, 0, 0.3, 0.6] as const, rng),
  };
}

function randomKaleN(mode: DotStudioMode, rng: () => number): number {
  if (mode === 'breakthrough') {
    return pick([6, 7, 8, 9, 10, 12] as const, rng);
  }
  return rng() < 0.72 ? 1 : pick([2, 3, 4, 6] as const, rng);
}

export interface RandomDotStudioPattern {
  readonly variant: DotStudioMode;
  readonly seedText: string;
  readonly overrides: DensityConfigOverrides;
}

/**
 * Port of v9.1 `randomizeAll()` — picks a calm dot-studio mode, seed, and
 * density-field source/symmetry params. Dot-grid sliders (spacing, minDot…)
 * stay on the mosaic knob panel.
 */
export function dotStudioPatternForMode(
  mode: DotStudioMode,
  rng: () => number = Math.random,
): RandomDotStudioPattern {
  const seedText = randomMorfiSeedText(rng);
  return {
    variant: mode,
    seedText,
    overrides: {
      ...randomSourceOverrides(rng),
      kaleN: randomKaleN(mode, rng),
    },
  };
}

export function randomizeDotStudioPattern(rng: () => number = Math.random): RandomDotStudioPattern {
  const variant = pick(PATTERN_PICKER_MODES, rng);
  return dotStudioPatternForMode(variant, rng);
}

/** One random cover plus three fixed preset alternates for the hero picker. */
export function generateHeroCoverPickerOptions(
  rng: () => number = Math.random,
): readonly [
  RandomDotStudioPattern,
  RandomDotStudioPattern,
  RandomDotStudioPattern,
  RandomDotStudioPattern,
] {
  return [
    randomizeDotStudioPattern(rng),
    dotStudioPatternForMode(HERO_COVER_ALT_MODES[0], rng),
    dotStudioPatternForMode(HERO_COVER_ALT_MODES[1], rng),
    dotStudioPatternForMode(HERO_COVER_ALT_MODES[2], rng),
  ];
}
